/**
 * useSongActions — 一首歌嘅**輕量動作**（揀版本／改歌名）
 *
 * 血緣：`decisions/027`（撳卡揀版本）＋ `decisions/039`（改歌名；產品負責人：「i want the song name can be edit」）
 * 由 `pages/SongsPage.tsx` 抽出（`P-90`：頁面檔案硬規則 ≤200 行；動作邏輯唔應該佔頁面）
 *
 * ⚠️ **失敗一律照實報**（`onError`；唔准扮成功）；成功 → `onUpdated(更新後嘅歌)`（父層負責 merge
 *   library ＋（如果係正在播嗰首）`player.renameTrack`）
 * ⚠️ 兩個動作都**零 credits**（`/pick` 唔會叫 provider；改歌名只寫 DB）
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSessionId } from './useChatStream';
import type { LibrarySong } from './useLibrary';

export function useSongActions(opts: {
  deviceId: string | null;
  /** 成功 → 更新後嘅歌（父層 merge 落 library） */
  onUpdated: (song: LibrarySong) => void;
  /** 失敗 → 照實講（父層顯示） */
  onError: (msg: string) => void;
}) {
  const { deviceId, onUpdated, onError } = opts;
  const { t } = useTranslation();
  const [busyId, setBusyId] = useState<string | null>(null);

  /** 共用：一個「叫 API → 照實回報」嘅呼叫（**唔准**靜靜失敗） */
  const call = useCallback(
    async (id: string, url: string, init: RequestInit): Promise<void> => {
      setBusyId(id);
      try {
        const res = await fetch(url, init);
        const data = (await res.json().catch(() => ({}))) as { song?: LibrarySong; error?: string };
        if (!res.ok || !data.song) {
          onError(`${t('song_error_generic')}（${data.error ?? `http_${res.status}`}）`);
          return;
        }
        onUpdated(data.song);
      } catch (err) {
        onError(`${t('song_error_generic')}（${String(err).slice(0, 80)}）`);
      } finally {
        setBusyId(null);
      }
    },
    [onError, onUpdated, t],
  );

  /** ⭐ **改歌名**（`PATCH /api/song/:id/title`；零 credits —— 唔會叫 provider） */
  const renameTitle = useCallback(
    (id: string, title: string) =>
      call(id, `/api/song/${id}/title`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: getSessionId(), deviceId, title }),
      }),
    [call, deviceId],
  );

  /** ⭐ **揀版本**（多版本；`/pick` **唔會**再花 provider 錢） */
  const pickVariant = useCallback(
    (id: string, variantIndex: number) =>
      call(id, `/api/song/${id}/pick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: getSessionId(), deviceId, index: variantIndex }),
      }),
    [call, deviceId],
  );

  return { busyId, renameTitle, pickVariant };
}
